import { useState } from 'react';
import './Page.css';
import { PortalIframe } from './PortalIframe';
import { useQuestionCounts, formatCount } from '../hooks/useQuestionCounts';

export default function SATPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const counts = useQuestionCounts();

  const faq = [
    {
      q: 'How is Scholar Sphere different from apps like Acely or Khan Academy?',
      a: `Other platforms give you software and call it a day. Scholar Sphere combines a ${formatCount(counts.sat.total)} question bank, full review mode, and an AI assistant with real human expert tutors available for live 1:1 sessions. You get the convenience of self-paced practice plus a real person in your corner.`,
    },
    {
      q: "What's the score improvement guarantee?",
      a: "We guarantee a meaningful improvement from your baseline. Students who complete their personalized study plan and attend scheduled sessions consistently see 140+ point gains on average. If you don't improve after completing the program requirements, we will work with you — at no extra charge — until you do.",
    },
    {
      q: 'Who should register — the parent or the student?',
      a: "The parent or guardian should register using their own email address. After creating the account, you'll be prompted to add the student under your profile.",
    },
    {
      q: 'What does the free sign-up include?',
      a: `Free access includes a diagnostic assessment, a sample of practice questions across every SAT topic, and a look at your dashboard with accuracy tracking. Upgrade to unlock the full ${formatCount(counts.sat.total)} question bank, unlimited AI assistant, and full review mode.`,
    },
    {
      q: "What's the difference between the Question Bank and 1:1 Tutoring?",
      a: 'The Question Bank is self-paced — practice any time, review your sessions in detail, and ask your AI assistant for explanations. 1:1 Tutoring is fully personalized private sessions built around your specific weaknesses and schedule. Many students combine both for the best results.',
    },
    {
      q: 'Can I combine the Question Bank with live tutoring?',
      a: 'Yes — and we recommend it. Many students use the question bank for daily practice and add 1:1 tutoring sessions weekly for strategy coaching and targeted help on weak areas.',
    },
    {
      q: 'How quickly can I see results?',
      a: 'Students who practice consistently in the question bank typically see measurable accuracy gains within 2–3 weeks. Most students see 100+ point improvement after 6–8 weeks of structured prep.',
    },
    {
      q: 'Can I speak with someone before signing up?',
      a: 'Yes — we offer a free 15-minute consultation with our program director. No pressure, just honest advice on which path is right for your student.',
    },
  ];

  return (
    <div className="page-wrap">

      {/* ── Hero ── */}
      <div className="exam-hero">
        <div className="exam-hero-inner">
          <div className="exam-hero-text">
            <div className="exam-hero-badge">SAT Prep</div>
            <h1>2,000+ SAT questions.<br />Real tutors. Real results.</h1>
            <p className="exam-hero-sub">
              Practice every SAT topic, review every session in full detail, and get
              AI-powered explanations on every answer. Add a live expert tutor when
              you want a human in your corner.
            </p>
            <p className="exam-hero-proof">★★★★★ &nbsp; 97% of students improve · 150+ avg point gain</p>
            <div className="exam-hero-btns">
              <a href="/signup" className="btn-gold">Start Practicing Free</a>
              <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
            </div>
          </div>
          <div className="exam-hero-visual" style={{ alignItems: 'center', paddingBottom: 32 }}>
            <PortalIframe src="/preview/practice" naturalHeight={580} displayUrl="scholar-sphere.com/practice" />
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
  
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">{formatCount(counts.sat.total)}</div>
              <div className="exam-stat-label">SAT Practice Questions</div>
            </div>
            <div>
              <div className="exam-stat-num">150+</div>
              <div className="exam-stat-label">Avg Point Gain</div>
            </div>
            <div>
              <div className="exam-stat-num">97%</div>
              <div className="exam-stat-label">Students Improve</div>
            </div>
            <div>
              <div className="exam-stat-num">$55/hr</div>
              <div className="exam-stat-label">Live Expert Tutoring</div>
            </div>
          </div>
        </div>
      </div>
      <div className="container page-body" style={{ paddingTop: 80 }}>

        {/* ── Score wins ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32}}>
            <span className="section-label">Real Results</span>
            <h2 className="section-heading" style={{ marginBottom: 8, marginTop:8 }}>Students who put in the work, win.</h2>
            <p className="section-sub">These aren't outliers. They're what consistent practice + expert guidance looks like.</p>
          </div>
          <div className="score-wins">
            <div className="score-win-card">
              <div className="score-win-scores">
                <span className="score-win-before">1210</span>
                <span className="score-win-arrow">→</span>
                <span className="score-win-after">1380</span>
              </div>
              <span className="score-win-gain">+170 points</span>
              <div className="score-win-name">Samir K., Grade 11</div>
              <div className="score-win-detail">6 weeks · Question Bank + Tutoring</div>
            </div>
            <div className="score-win-card">
              <div className="score-win-scores">
                <span className="score-win-before">1100</span>
                <span className="score-win-arrow">→</span>
                <span className="score-win-after">1380</span>
              </div>
              <span className="score-win-gain">+280 points</span>
              <div className="score-win-name">Kavita J., Student</div>
              <div className="score-win-detail">10 sessions · 1:1 Tutoring</div>
            </div>
            <div className="score-win-card">
              <div className="score-win-scores">
                <span className="score-win-before">1280</span>
                <span className="score-win-arrow">→</span>
                <span className="score-win-after">1510</span>
              </div>
              <span className="score-win-gain">+230 points</span>
              <div className="score-win-name">Sarah M., Student</div>
              <div className="score-win-detail">8 weeks · Question Bank + Tutoring</div>
            </div>
          </div>
        </div>

        {/* ── What's covered ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <span className="section-label">Full Coverage</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Every SAT topic. Every subtopic.</h2>
            <p className="section-sub">Questions organized exactly the way the digital SAT tests them.</p>
          </div>
          <div className="topic-grid">
            <div className="topic-card">
              <div className="topic-icon">📐</div>
              <div>
                <div className="topic-card-name">Math — {counts.sat.math.toLocaleString()} questions</div>
                <div className="topic-card-subtopics">Algebra · Advanced Math · Problem Solving & Data Analysis · Geometry & Trigonometry</div>
              </div>
            </div>
            <div className="topic-card">
              <div className="topic-icon">📖</div>
              <div>
                <div className="topic-card-name">Reading & Writing — {counts.sat.english.toLocaleString()} questions</div>
                <div className="topic-card-subtopics">Craft & Structure · Information & Ideas · Standard English Conventions · Expression of Ideas</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Feature 1: Question Bank ── */}
        <div className="feature-row">
          <div className="feature-row-text">
            <div className="feature-row-eyebrow">Question Bank</div>
            <h2>Practice the topics that actually show up on test day.</h2>
            <p>
              {formatCount(counts.sat.total)} SAT questions organized by topic and subtopic. Drill Algebra until you own it,
              then switch to Standard English. Every question tracks your accuracy so you always
              know where you stand and what to focus on next.
            </p>
            <ul className="check-list">
              <li>{counts.sat.total.toLocaleString()} total SAT questions across Math and Reading & Writing</li>
              <li>Organized by topic — practice exactly what you need</li>
              <li>Difficulty levels from foundational to exam-level</li>
              <li>Time-per-question tracking to sharpen your pacing</li>
            </ul>
            <a href="/signup" className="btn-gold">Try It Free</a>
          </div>
          <div className="feature-row-visual" style={{ background: 'transparent', border: 'none', padding: 0 }}>
            <PortalIframe src="/preview/practice" naturalHeight={580} displayUrl="scholar-sphere.com/practice" />
          </div>
        </div>

        {/* ── Feature 2: Review Mode ── */}
        <div className="feature-row reverse">
          <div className="feature-row-text">
            <div className="feature-row-eyebrow">Review Mode</div>
            <h2>Learn more from what you got wrong.</h2>
            <p>
              After every session, Review Mode shows you every question — your answer, the correct
              answer, and a detailed AI explanation side by side. Most students learn more in
              10 minutes of review than in another hour of new questions.
            </p>
            <ul className="check-list">
              <li>Every past session saved and reviewable any time</li>
              <li>See exactly which concepts cost you the most points</li>
              <li>AI assistant explains each mistake in plain language</li>
              <li>Spot recurring patterns across multiple sessions</li>
            </ul>
            <a href="/signup" className="btn-navy">See It in Action</a>
          </div>
          <div className="feature-row-visual" style={{ background: 'transparent', border: 'none', padding: 0 }}>
            <PortalIframe src="/preview/dashboard" naturalHeight={720} displayUrl="scholar-sphere.com/subjects/sat" />
          </div>
        </div>

        {/* ── Comparison ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">The Difference</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Software alone won't get you to 1500.</h2>
            <p className="section-sub">Apps give you questions. Scholar Sphere gives you questions, expert analysis, and a real human who knows your name.</p>
          </div>
          <div style={{ overflowX: 'auto', marginBottom: 32 }}>
            <table className="vs-table">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Feature</th>
                  <th className="col-us">Scholar Sphere</th>
                  <th className="col-them">Software-Only Apps</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>SAT Practice Questions</td>
                  <td className="col-us vs-yes">{formatCount(counts.sat.total)}</td>
                  <td>Varies</td>
                </tr>
                <tr>
                  <td>Full Session Review Mode</td>
                  <td className="col-us vs-yes">✓ Every question, every answer</td>
                  <td className="vs-partial">Limited</td>
                </tr>
                <tr>
                  <td>AI-Powered Answer Explanations</td>
                  <td className="col-us vs-yes">✓ On every question</td>
                  <td className="vs-partial">On some</td>
                </tr>
                <tr>
                  <td>Real Human Expert Tutors</td>
                  <td className="col-us vs-yes">✓ Live 1:1</td>
                  <td className="vs-no">✗ None</td>
                </tr>
                <tr>
                  <td>Topic-by-Topic Accuracy Tracking</td>
                  <td className="col-us vs-yes">✓</td>
                  <td className="vs-yes">✓</td>
                </tr>
                <tr>
                  <td>Score Improvement Guarantee</td>
                  <td className="col-us vs-yes">✓ 140+ pts or we keep working</td>
                  <td className="vs-partial">Partial / conditional</td>
                </tr>
                <tr>
                  <td>College Counseling & Essay Support</td>
                  <td className="col-us vs-yes">✓</td>
                  <td className="vs-no">✗</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Score Guarantee ── */}
        <div className="guarantee-banner">
          <div className="guarantee-icon">🛡️</div>
          <h2 className="guarantee-heading">140+ Points or We Keep Working</h2>
          <p className="guarantee-sub">
            Every student who completes their personalized study plan sees a meaningful improvement —
            we guarantee it. If you don't hit your target after meeting program requirements,
            we work with you at no extra charge.
          </p>
          <a href="/contact" className="btn-gold">Talk to Us About Your Goal →</a>
        </div>

        {/* ── Pricing ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">Choose Your Path</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Two ways to prep. Both backed by experts.</h2>
            <p className="section-sub">Start free with the question bank. Add live tutoring whenever you're ready.</p>
          </div>
          <div className="two-col-section" style={{ marginBottom: 0 }}>
            <div className="path-card featured">
              <div className="path-icon">📚</div>
              <div className="path-name">Question Bank</div>
              <div className="path-tagline">Self-paced, anytime</div>
              <div className="path-price">$9.99<span style={{ fontSize: 16, fontWeight: 400 }}>/mo</span></div>
              <div className="path-price-note">Includes Aira AI tutor · Cancel any time</div>
              <ul className="path-features">
                <li>{formatCount(counts.sat.total)} SAT questions by topic &amp; difficulty</li>
                <li>Full review mode after every session</li>
                <li>AI assistant explains every answer</li>
                <li>Topic-by-topic accuracy dashboard</li>
                <li>ACT, AP Physics &amp; Calculus also included</li>
              </ul>
              <a href="/signup" className="btn-gold" style={{ textAlign: 'center' }}>Start Free</a>
            </div>
            <div className="path-card">
              <div className="path-icon">👨‍🏫</div>
              <div className="path-name">1:1 Private Tutoring</div>
              <div className="path-tagline">Maximum personalization</div>
              <div className="path-price">$55<span style={{ fontSize: 16, fontWeight: 400 }}>/hour</span></div>
              <div className="path-price-note">10 &amp; 20-hour bundles available</div>
              <ul className="path-features">
                <li>99th percentile SAT scorer as your tutor</li>
                <li>Sessions built around your exact weak areas</li>
                <li>Start practicing for free + full score report</li>
                <li>Custom homework &amp; strategy between sessions</li>
                <li>Flexible weekday &amp; weekend Zoom scheduling</li>
              </ul>
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-navy" style={{ textAlign: 'center' }}>Book a Session</a>
            </div>
          </div>
        </div>

        {/* ── Testimonials ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">Student Stories</span>
            <h2 className="section-heading">In their own words.</h2>
          </div>
          <div className="mini-test-grid">
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8, fontSize: 15 }}>★★★★★</div>
              <p className="mini-test-quote">"Reading comprehension was always my biggest challenge. After 10 private sessions focused on active reading strategies and time management, my score jumped by 120 points. I finally feel confident."</p>
              <div className="mini-test-name">Kavita J. — Student</div>
            </div>
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8, fontSize: 15 }}>★★★★★</div>
              <p className="mini-test-quote">"I went from a 1210 to a 1380 after just six weeks. Practicing daily in the question bank and reviewing every session made the difference."</p>
              <div className="mini-test-name">Samir K. — Grade 11</div>
            </div>
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8, fontSize: 15 }}>★★★★★</div>
              <p className="mini-test-quote">"The question bank let me practice any topic whenever I had 20 minutes. Reviewing every past session showed me exactly where I kept losing points."</p>
              <div className="mini-test-name">Riya P. — IL</div>
            </div>
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8, fontSize: 15 }}>★★★★★</div>
              <p className="mini-test-quote">"My son connected with his tutor for 1:1 tailored SAT sessions, understood the strategies, and went into his test feeling genuinely prepared — not just practiced."</p>
              <div className="mini-test-name">Rajesh P. — Parent</div>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 24 }}>
            <h2 className="section-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faq.map((item, i) => (
              <details
                key={i}
                className="faq-item"
                open={openFaq === i}
                onClick={(e) => { e.preventDefault(); setOpenFaq(openFaq === i ? null : i); }}
              >
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="cta-strip" style={{marginBottom: 32}}>
          <h3>Ready to start? It's free.</h3>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15, marginBottom: 24 }}>
            Sign up and access the question bank in minutes. No credit card required. Add tutoring whenever you're ready.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/signup" className="btn-gold">Start Practicing Free</a>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-outline-white">Book a Free Consultation</a>
          </div>
        </div>

      </div>
    </div>
  );
}
